// `client-contact-search.page.tpl.html` — the contact criteria builder's outer shell.
//
// The candidate builder's twin (`CriteriaBuilderShell`) and NOT a parameterisation of it: the
// two have different root classes, different sections, and a section-order map the candidate
// form does not have at all. Six ids collapse into five visible sections, because Location and
// Company location share an order and are mutually exclusive.
import type { ReactElement, ReactNode } from 'react'
import { WooCollapsible } from '../common/WooCollapsible.tsx'

/**
 * `extensionCriteriaSectionOrder`
 * (extension-criteria-client-contact-search-section-order.service.ng1.ts).
 *
 * ⚠️ IT IS AN `order` STYLE ON A FLEX CONTAINER, NOT THE DOM ORDER. Upstream writes the
 * sections in template order and reorders them with `ng-style="{ order: … }"`, so the source
 * reads Contact, Company, Location, Company location, ATS, Keywords while the SCREEN reads
 * Contact, Company, Location, ATS, Keywords. Rendering in template order gives the ATS section
 * below the keywords one — which is also the trap in the left rail.
 */
export const CLIENT_CONTACT_SECTION_ORDER: Readonly<Record<string, number>> = {
  'contact-filters-criteria-id': 0,
  'company-filters-criteria-id': 1,
  'location-criteria-id': 2,
  'company-location-criteria-id': 2,
  'ats-filters-criteria-id': 3,
  'keywords-criteria-id': 4,
}

interface ClientContactSection {
  id: string
  className: string
  title: string
  /**
   * ⚠️ THE `*-wrap` DIV INSIDE EACH COLLAPSIBLE, WHICH THIS SHELL WAS NOT DRAWING.
   * Upstream's template puts one immediately inside every `woo-collapsible`
   * (`client-contact-search.page.tpl.html:9, 207, 428, 906, 964`), and the stylesheets are
   * written against it — `keyword-wrap criteria-table` is what gives the keyword section its
   * three-column grid, so without it the Field / Condition / Keywords header row has no
   * table to head and the selects stack. The candidate shell has carried `wrapClassName`
   * since it was written; this one had the field and never used it.
   *
   * Two sections draw their own: Contact filters opens with `switched-companies-wrap` and
   * Company filters with `company-filters-wrap`, both inside their components.
   */
  wrapClassName?: string
}

/** In template order; the `order` style below is what puts them on screen in the right one. */
const SECTIONS: readonly ClientContactSection[] = [
  { id: 'contact-filters-criteria-id', className: 'contact-filters', title: 'Contact filters' },
  {
    id: 'company-filters-criteria-id',
    className: 'company-filters',
    title: 'Company filters',
    wrapClassName: 'company-filters-wrap',
  },
  { id: 'location-criteria-id', className: 'location', title: 'Location', wrapClassName: 'location-wrap' },
  {
    id: 'company-location-criteria-id',
    className: 'company-location',
    title: 'Company location',
    wrapClassName: 'location-wrap',
  },
  { id: 'ats-filters-criteria-id', className: 'ats-filters', title: '', wrapClassName: 'ats-filters-wrap' },
  {
    id: 'keywords-criteria-id',
    className: 'keywords',
    title: 'Keyword search',
    wrapClassName: 'keyword-wrap criteria-table',
  },
]

export interface ClientContactCriteriaShellProps {
  /** Names the ATS section — `collapsible-title="{{::ctrl.atsLabel}} filters"`. */
  atsLabel?: string
  /** Whether the ATS section exists at all; see `clientContactNavigationOptions`. */
  hasAtsFilters?: boolean
  /** Swaps Location for Company location — never both. */
  jobCompanyInfoEnabled?: boolean
  sections?: Partial<Record<string, ReactNode>>
}

export function ClientContactCriteriaShell({
  atsLabel,
  hasAtsFilters = false,
  jobCompanyInfoEnabled = false,
  sections = {},
}: ClientContactCriteriaShellProps): ReactElement {
  const visible = SECTIONS.filter((section) => {
    if (section.id === 'location-criteria-id') return !jobCompanyInfoEnabled
    if (section.id === 'company-location-criteria-id') return jobCompanyInfoEnabled
    if (section.id === 'ats-filters-criteria-id') return hasAtsFilters
    return true
  })

  return (
    // ⚠️ BOTH CLASSES, ON BOTH ELEMENTS. The page is `client-contact-search-page
    // chrome-extension-search-page` and the container `client-contact-criteria-container
    // criteria-container` — the second of each is what the shared search stylesheet is written
    // against, and the first carries the contact form's own overrides. Dropping either loses a
    // sheet.
    <div className="client-contact-search-page chrome-extension-search-page">
      <div className="client-contact-criteria-container criteria-container">
        {visible.map((section) => (
          <WooCollapsible
            key={section.id}
            id={section.id}
            title={section.id === 'ats-filters-criteria-id' ? `${atsLabel ?? ''} filters` : section.title}
            className={section.className}
            style={{ order: CLIENT_CONTACT_SECTION_ORDER[section.id] }}
            startOpen
          >
            {section.wrapClassName === undefined ? (
              (sections[section.id] ?? null)
            ) : (
              <div className={section.wrapClassName}>{sections[section.id] ?? null}</div>
            )}
          </WooCollapsible>
        ))}
      </div>
    </div>
  )
}
